import { useMemo } from 'react'

import { SupporterTabs, type SupporterDetailsType } from './SupporterTabs'
import type {
  SupporterShowQueryType,
  SupporterContactsQueryType,
  SupporterNotesQueryType,
} from '@frontend/graphql/queries/supporters'
import type { SmsConversationPage } from '../features/show-supporter/SmsConversation'
import type { EngagementLevelsIndexQueryType } from '@frontend/graphql/queries/engagementLevels'
import type { TagFiltersIndexQueryType } from '@frontend/graphql/queries/tagFilters'

import { SupporterShowCard } from '../features/edit-supporter/SupporterShowCard'
import { SupporterReloadProvider } from '../features/edit-supporter/SupporterReloadContext'

import { DialogProvider, type DialogRegistry } from '@components/DialogProvider'
import { SendSmsDialog } from '../features/send-sms/SendSmsDialog'
import { LogContactDialog } from '../features/log-contact/LogContactDialog'
import { ContactPreferencesDialog } from '../features/update-contact-preferences/ContactPreferencesDialog'
import { SendEmailDialog } from '../features/send-email/SendEmailDialog'
import { InitiateCallDialog } from '../features/initiate-call/InitiateCallDialog'
import type { SharedInertiaProps } from '../types/supporterTypes'

const supporterShowRegistry: DialogRegistry = {
  sendSms: SendSmsDialog,
  logContact: LogContactDialog,
  updateContactPreferences: ContactPreferencesDialog,
  sendEmail: SendEmailDialog,
  initiateCall: InitiateCallDialog,
}

export type SupporterProfileContentProps = {
  group: SupporterShowQueryType['group']
  contacts: SupporterContactsQueryType['contacts']
  supporterNotes: SupporterNotesQueryType['supporterNotes']
  supporterSmsMessages?: SmsConversationPage
  engagementLevels: EngagementLevelsIndexQueryType['engagementLevels']
  tagFilters: TagFiltersIndexQueryType['tagFilters']
  permissions: SharedInertiaProps['permissions']
  reloadKeys: string[]
}

export function SupporterProfileContent({
  group,
  group: { supporters, id: groupId },
  contacts,
  supporterNotes,
  supporterSmsMessages,
  engagementLevels,
  tagFilters,
  permissions,
  reloadKeys,
}: SupporterProfileContentProps) {
  const edge = supporters.edges[0]
  const supporterTagFilters = edge?.tagFilters
  const mappedSupporterTagFilters = useMemo(
    () => (supporterTagFilters ?? []).map((tagFilter) => ({ value: tagFilter.id, label: tagFilter.name })),
    [supporterTagFilters]
  )

  if (!edge) return null

  const { membership, unsubscriber, node: supporter } = edge

  return (
    <DialogProvider registry={supporterShowRegistry}>
      <SupporterReloadProvider reloadKeys={reloadKeys}>
        <SupporterShowCard
          group={group}
          supporter={supporter}
          membership={membership}
          unsubscriber={unsubscriber}
          canRemoveMembers={permissions.removeMembers}
          requestMemberRemovals={permissions.requestMemberRemovals}
          canSendIndividualSms={permissions.canSendIndividualSms}
          canMakePhoneCalls={permissions.canMakePhoneCalls}
          demoSite={permissions.demoSite}
        />

        <SupporterTabs
          supporter={supporter satisfies SupporterDetailsType}
          contacts={contacts}
          supporterNotes={supporterNotes}
          supporterSmsMessages={supporterSmsMessages}
          engagementLevels={engagementLevels}
          tagFilters={tagFilters}
          supporterTagFilters={mappedSupporterTagFilters}
          permissions={permissions}
          groupId={groupId}
        />
      </SupporterReloadProvider>
    </DialogProvider>
  )
}
